"use client"

import { useEffect, useRef } from "react"
import { useCursorContext } from "../components/CursorProvider"
import { mouseStore } from "../mouseStore"

interface UseCursorIdleOptions {
  /** Milliseconds of no movement before idle triggers (default 2000) */
  timeout?: number
  /** Cursor variant to activate on idle (default "spot") */
  variant?: string
  /** Called when idle state starts */
  onIdle?: () => void
  /** Called when cursor movement resumes */
  onWake?: () => void
}

/**
 * Switches to a different cursor variant when the cursor hasn't moved
 * for `timeout` ms. Reverts when movement resumes.
 */
export const useCursorIdle = ({
  timeout = 2000,
  variant = "spot",
  onIdle,
  onWake,
}: UseCursorIdleOptions = {}) => {
  const { setCursorVariant } = useCursorContext()

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const idleRef = useRef(false)
  const onIdleRef = useRef(onIdle)
  const onWakeRef = useRef(onWake)

  useEffect(() => { onIdleRef.current = onIdle }, [onIdle])
  useEffect(() => { onWakeRef.current = onWake }, [onWake])

  useEffect(() => {
    const arm = () => {
      if (timerRef.current) clearTimeout(timerRef.current)

      if (idleRef.current) {
        idleRef.current = false
        setCursorVariant("", "pop")
        onWakeRef.current?.()
      }

      timerRef.current = setTimeout(() => {
        idleRef.current = true
        setCursorVariant(variant, "push")
        onIdleRef.current?.()
      }, timeout)
    }

    // mouseStore replaces the direct window.mousemove listener —
    // no duplicate event handler alongside the other subscribers.
    const unsub = mouseStore.subscribe(arm)
    arm() // arm immediately so idle can fire even without mouse movement

    return () => {
      unsub()
      if (timerRef.current) clearTimeout(timerRef.current)
      if (idleRef.current) {
        idleRef.current = false
        setCursorVariant("", "pop")
      }
    }
  }, [timeout, variant, setCursorVariant])
  // setCursorVariant is now stable (useCallback in CursorProvider) — this
  // effect runs only when timeout or variant actually changes, not every frame.
}
